/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import {AUTO_STYLE, AnimateChildOptions, AnimateTimings, AnimationOptions, AnimationQueryOptions, ɵPRE_STYLE as PRE_STYLE, ɵStyleData} from '@angular/animations';

import {AnimationDriver} from '../render/animation_driver';
import {copyObj, copyStyles, interpolateParams, iteratorToArray, resolveTiming, resolveTimingValue} from '../util';

import {AnimateAst, AnimateChildAst, AnimateRefAst, Ast, AstVisitor, DynamicTimingAst, GroupAst, KeyframesAst, QueryAst, ReferenceAst, SequenceAst, StaggerAst, StateAst, StyleAst, TimingAst, TransitionAst, TriggerAst} from './animation_ast';
import {AnimationTimelineInstruction, createTimelineInstruction} from './animation_timeline_instruction';
import {ElementInstructionMap} from './element_instruction_map';

const /** @type {?} */ ONE_FRAME_IN_MILLISECONDS = 1;
/**
 * @param {?} driver
 * @param {?} rootElement
 * @param {?} ast
 * @param {?=} startingStyles
 * @param {?=} finalStyles
 * @param {?=} options
 * @param {?=} subInstructions
 * @param {?=} errors
 * @return {?}
 */
export function buildAnimationTimelines(
    driver: AnimationDriver, rootElement: any, ast: Ast, startingStyles: ɵStyleData = {},
    finalStyles: ɵStyleData = {}, options: AnimationOptions,
    subInstructions?: ElementInstructionMap, errors: any[] = []): AnimationTimelineInstruction[] {
  return new AnimationTimelineBuilderVisitor().buildKeyframes(
      driver, rootElement, ast, startingStyles, finalStyles, options, subInstructions, errors);
}
export class AnimationTimelineBuilderVisitor implements AstVisitor {
/**
 * @param {?} driver
 * @param {?} rootElement
 * @param {?} ast
 * @param {?} startingStyles
 * @param {?} finalStyles
 * @param {?} options
 * @param {?=} subInstructions
 * @param {?=} errors
 * @return {?}
 */
buildKeyframes(
      driver: AnimationDriver, rootElement: any, ast: Ast, startingStyles: ɵStyleData,
      finalStyles: ɵStyleData, options: AnimationOptions, subInstructions?: ElementInstructionMap,
      errors: any[] = []): AnimationTimelineInstruction[] {
    subInstructions = subInstructions || new ElementInstructionMap();
    const /** @type {?} */ context = new AnimationTimelineContext(driver, rootElement, subInstructions, errors, []);
    context.options = options;
    context.currentTimeline.setStyles([startingStyles], null, context.errors, options);

    ast.visit(this, context);

    // this checks to see if an actual animation happened
    const /** @type {?} */ timelines = context.timelines.filter(timeline => timeline.containsAnimation());
    if (timelines.length && Object.keys(finalStyles).length) {
      const /** @type {?} */ tl = timelines[timelines.length - 1];
      if (!tl.allowOnlyTimelineStyles()) {
        tl.setStyles([finalStyles], null, context.errors, options);
      }
    }

    return timelines.length ? timelines.map(timeline => timeline.buildKeyframes()) :
                              [createTimelineInstruction(rootElement, [], [], [], 0, 0, '', false)];
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitTrigger(ast: TriggerAst, context: AnimationTimelineContext): any {
    // these values are not visited in this AST
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitState(ast: StateAst, context: AnimationTimelineContext): any {
    // these values are not visited in this AST
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitTransition(ast: TransitionAst, context: AnimationTimelineContext): any {
    // these values are not visited in this AST
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitAnimateChild(ast: AnimateChildAst, context: AnimationTimelineContext): any {
    const /** @type {?} */ elementInstructions = context.subInstructions.consume(context.element);
    if (elementInstructions) {
      const /** @type {?} */ innerContext = context.createSubContext(ast.options);
      const /** @type {?} */ startTime = context.currentTimeline.currentTime;
      const /** @type {?} */ endTime = this._visitSubInstructions(
          elementInstructions, innerContext, /** @type {?} */(( innerContext.options as AnimateChildOptions)));
      if (startTime != endTime) {
        // we do this on the upper context because we created a sub context for
        // the sub child animations
        context.transformIntoNewTimeline(endTime);
      }
    }
    context.previousNode = ast;
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitAnimateRef(ast: AnimateRefAst, context: AnimationTimelineContext): any {
    const /** @type {?} */ innerContext = context.createSubContext(ast.options);
    innerContext.transformIntoNewTimeline();
    this.visitReference(ast.animation, innerContext);
    context.transformIntoNewTimeline(innerContext.currentTimeline.currentTime);
    context.previousNode = ast;
  }
/**
 * @param {?} instructions
 * @param {?} context
 * @param {?} options
 * @return {?}
 */
private _visitSubInstructions(
      instructions: AnimationTimelineInstruction[], context: AnimationTimelineContext,
      options: AnimateChildOptions): number {
    const /** @type {?} */ startTime = context.currentTimeline.currentTime;
    let /** @type {?} */ furthestTime = startTime;

    // this is a special-case for when a user wants to skip a sub
    // animation from being fired entirely.
    const /** @type {?} */ duration = options.duration != null ? resolveTimingValue(options.duration) : null;
    const /** @type {?} */ delay = options.delay != null ? resolveTimingValue(options.delay) : null;
    if (duration !== 0) {
      instructions.forEach(instruction => {
        const /** @type {?} */ instructionTimings =
            context.appendInstructionToTimeline(instruction, duration, delay);
        furthestTime =
            Math.max(furthestTime, instructionTimings.duration + instructionTimings.delay);
      });
    }

    return furthestTime;
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitReference(ast: ReferenceAst, context: AnimationTimelineContext) {
    context.updateOptions(ast.options, true);
    ast.animation.visit(this, context);
    context.previousNode = ast;
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitSequence(ast: SequenceAst, context: AnimationTimelineContext) {
    const /** @type {?} */ subContextCount = context.subContextCount;
    let /** @type {?} */ ctx = context;
    const /** @type {?} */ options = ast.options;

    if (options && (options.params || options.delay)) {
      ctx = context.createSubContext(options);
      ctx.transformIntoNewTimeline();

      if (options.delay != null) {
        if (ctx.previousNode instanceof StyleAst) {
          ctx.currentTimeline.snapshotCurrentStyles();
          ctx.previousNode = DEFAULT_NOOP_PREVIOUS_NODE;
        }

        const /** @type {?} */ delay = resolveTimingValue(options.delay);
        ctx.delayNextStep(delay);
      }
    }

    if (ast.steps.length) {
      ast.steps.forEach(s => s.visit(this, ctx));

      // this is here just incase the inner steps only contain or end with a style() call
      ctx.currentTimeline.applyStylesToKeyframe();

      // this means that some animation function within the sequence
      // ended up creating a sub timeline (which means the current
      // timeline cannot overlap with the contents of the sequence)
      if (ctx.subContextCount > subContextCount) {
        ctx.transformIntoNewTimeline();
      }
    }

    context.previousNode = ast;
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitGroup(ast: GroupAst, context: AnimationTimelineContext) {
    const /** @type {?} */ innerTimelines: TimelineBuilder[] = [];
    let /** @type {?} */ furthestTime = context.currentTimeline.currentTime;
    const /** @type {?} */ delay = ast.options && ast.options.delay ? resolveTimingValue(ast.options.delay) : 0;

    ast.steps.forEach(s => {
      const /** @type {?} */ innerContext = context.createSubContext(ast.options);
      if (delay) {
        innerContext.delayNextStep(delay);
      }

      s.visit(this, innerContext);
      furthestTime = Math.max(furthestTime, innerContext.currentTimeline.currentTime);
      innerTimelines.push(innerContext.currentTimeline);
    });

    // this operation is run after the AST loop because otherwise
    // if the parent timeline's collected styles were updated then
    // it would pass in invalid data into the new-to-be forked items
    innerTimelines.forEach(
        timeline => context.currentTimeline.mergeTimelineCollectedStyles(timeline));
    context.transformIntoNewTimeline(furthestTime);
    context.previousNode = ast;
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitTiming(ast: TimingAst, context: AnimationTimelineContext): AnimateTimings {
    if (ast instanceof DynamicTimingAst) {
      const /** @type {?} */ strValue = context.params ?
          interpolateParams(ast.value, context.params, context.errors) :
          ast.value.toString();
      return resolveTiming(strValue, context.errors);
    } else {
      return {duration: ast.duration, delay: ast.delay, easing: ast.easing};
    }
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitAnimate(ast: AnimateAst, context: AnimationTimelineContext) {
    const /** @type {?} */ timings = context.currentAnimateTimings = this.visitTiming(ast.timings, context);
    const /** @type {?} */ timeline = context.currentTimeline;
    if (timings.delay) {
      context.incrementTime(timings.delay);
      timeline.snapshotCurrentStyles();
    }

    const /** @type {?} */ style = ast.style;
    if (style instanceof KeyframesAst) {
      this.visitKeyframes(style, context);
    } else {
      context.incrementTime(timings.duration);
      this.visitStyle( /** @type {?} */((style as StyleAst)), context);
      timeline.applyStylesToKeyframe();
    }

    context.currentAnimateTimings = null;
    context.previousNode = ast;
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitStyle(ast: StyleAst, context: AnimationTimelineContext) {
    const /** @type {?} */ timeline = context.currentTimeline;
    const /** @type {?} */ timings = /** @type {?} */(( context.currentAnimateTimings));

    // this is a special case for when a style() call
    // directly follows  an animate() call (but not inside of an animate() call)
    if (!timings && timeline.getCurrentStyleProperties().length) {
      timeline.forwardFrame();
    }

    const /** @type {?} */ easing = (timings && timings.easing) || ast.easing;
    if (ast.isEmptyStep) {
      timeline.applyEmptyStep(easing);
    } else {
      timeline.setStyles(ast.styles, easing, context.errors, context.options);
    }

    context.previousNode = ast;
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitKeyframes(ast: KeyframesAst, context: AnimationTimelineContext) {
    const /** @type {?} */ currentAnimateTimings = /** @type {?} */(( context.currentAnimateTimings));
    const /** @type {?} */ startTime = ( /** @type {?} */((context.currentTimeline))).duration;
    const /** @type {?} */ duration = currentAnimateTimings.duration;
    const /** @type {?} */ innerContext = context.createSubContext();
    const /** @type {?} */ innerTimeline = innerContext.currentTimeline;
    innerTimeline.easing = currentAnimateTimings.easing;

    ast.styles.forEach(step => {
      const /** @type {?} */ offset: number = step.offset || 0;
      innerTimeline.forwardTime(offset * duration);
      innerTimeline.setStyles(step.styles, step.easing, context.errors, context.options);
      innerTimeline.applyStylesToKeyframe();
    });

    // this will ensure that the parent timeline gets all the styles from
    // the child even if the new timeline below is not used
    context.currentTimeline.mergeTimelineCollectedStyles(innerTimeline);

    // we do this because the window between this timeline and the sub timeline
    // should ensure that the styles within are exactly the same as they were before
    context.transformIntoNewTimeline(startTime + duration);
    context.previousNode = ast;
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitQuery(ast: QueryAst, context: AnimationTimelineContext) {
    // in the event that the first step before this is a style step we need
    // to ensure the styles are applied before the children are animated
    const /** @type {?} */ startTime = context.currentTimeline.currentTime;
    const /** @type {?} */ options = /** @type {?} */(( (ast.options || {}) as AnimationQueryOptions));
    const /** @type {?} */ delay = options.delay ? resolveTimingValue(options.delay) : 0;

    if (delay && (context.previousNode instanceof StyleAst ||
                  (startTime == 0 && context.currentTimeline.getCurrentStyleProperties().length))) {
      context.currentTimeline.snapshotCurrentStyles();
      context.previousNode = DEFAULT_NOOP_PREVIOUS_NODE;
    }

    let /** @type {?} */ furthestTime = startTime;
    const /** @type {?} */ elms = context.invokeQuery(
        ast.selector, ast.originalSelector, ast.limit, ast.includeSelf,
        options.optional ? true : false, context.errors);

    context.currentQueryTotal = elms.length;
    let /** @type {?} */ sameElementTimeline: TimelineBuilder|null = null;
    elms.forEach((element, i) => {

      context.currentQueryIndex = i;
      const /** @type {?} */ innerContext = context.createSubContext(ast.options, element);
      if (delay) {
        innerContext.delayNextStep(delay);
      }

      if (element === context.element) {
        sameElementTimeline = innerContext.currentTimeline;
      }

      ast.animation.visit(this, innerContext);

      // this is here just incase the inner steps only contain or end
      // with a style() call (which is here to signal that this is a preparatory
      // call to style an element before it is animated again)
      innerContext.currentTimeline.applyStylesToKeyframe();

      const /** @type {?} */ endTime = innerContext.currentTimeline.currentTime;
      furthestTime = Math.max(furthestTime, endTime);
    });

    context.currentQueryIndex = 0;
    context.currentQueryTotal = 0;
    context.transformIntoNewTimeline(furthestTime);

    if (sameElementTimeline) {
      context.currentTimeline.mergeTimelineCollectedStyles(sameElementTimeline);
      context.currentTimeline.snapshotCurrentStyles();
    }

    context.previousNode = ast;
  }
/**
 * @param {?} ast
 * @param {?} context
 * @return {?}
 */
visitStagger(ast: StaggerAst, context: AnimationTimelineContext) {
    const /** @type {?} */ parentContext = /** @type {?} */(( context.parentContext));
    const /** @type {?} */ tl = context.currentTimeline;
    const /** @type {?} */ timings = ast.timings;
    const /** @type {?} */ duration = Math.abs(timings.duration);
    const /** @type {?} */ maxTime = duration * (context.currentQueryTotal - 1);
    let /** @type {?} */ delay = duration * context.currentQueryIndex;

    let /** @type {?} */ staggerTransformer = timings.duration < 0 ? 'reverse' : timings.easing;
    switch (staggerTransformer) {
      case 'reverse':
        delay = maxTime - delay;
        break;
      case 'full':
        delay = parentContext.currentStaggerTime;
        break;
    }

    const /** @type {?} */ timeline = context.currentTimeline;
    if (delay) {
      timeline.delayNextStep(delay);
    }

    const /** @type {?} */ startingTime = timeline.currentTime;
    ast.animation.visit(this, context);
    context.previousNode = ast;

    // time = duration + delay
    // the reason why this computation is so complex is because
    // the inner timeline may either have a delay value or a stretched
    // keyframe depending on if a subtimeline is not used or is used.
    parentContext.currentStaggerTime =
        (tl.currentTime - startingTime) + (tl.startTime - parentContext.currentTimeline.startTime);
  }
}

export declare type StyleAtTime = {
  time: number; value: string | number;
};

const /** @type {?} */ DEFAULT_NOOP_PREVIOUS_NODE = /** @type {?} */(( <Ast>{}));
export class AnimationTimelineContext {
public parentContext: AnimationTimelineContext|null = null;
public currentTimeline: TimelineBuilder;
public currentAnimateTimings: AnimateTimings|null = null;
public previousNode: Ast = DEFAULT_NOOP_PREVIOUS_NODE;
public subContextCount = 0;
public options: AnimationOptions = {};
public currentQueryIndex: number = 0;
public currentQueryTotal: number = 0;
public currentStaggerTime: number = 0;
/**
 * @param {?} _driver
 * @param {?} element
 * @param {?} subInstructions
 * @param {?} errors
 * @param {?} timelines
 * @param {?=} initialTimeline
 */
constructor(
private _driver: AnimationDriver,
public element: any,
public subInstructions: ElementInstructionMap,
public errors: any[],
public timelines: TimelineBuilder[], initialTimeline?: TimelineBuilder) {
    this.currentTimeline = initialTimeline || new TimelineBuilder(element, 0);
    timelines.push(this.currentTimeline);
  }
/**
 * @return {?}
 */
get params() { return this.options.params; }
/**
 * @param {?} options
 * @param {?=} skipIfExists
 * @return {?}
 */
updateOptions(options: AnimationOptions|null, skipIfExists?: boolean) {
    if (!options) return;

    const /** @type {?} */ newOptions = /** @type {?} */(( options as any));
    let /** @type {?} */ optionsToUpdate = this.options;

    // NOTE: this will get patched up when other animation methods support duration overrides
    if (newOptions.duration != null) {
      ( /** @type {?} */((optionsToUpdate as any))).duration = resolveTimingValue(newOptions.duration);
    }

    if (newOptions.delay != null) {
      optionsToUpdate.delay = resolveTimingValue(newOptions.delay);
    }

    const /** @type {?} */ newParams = newOptions.params;
    if (newParams) {
      let /** @type {?} */ paramsToUpdate: {[name: string]: any} = /** @type {?} */(( optionsToUpdate.params));
      if (!paramsToUpdate) {
        paramsToUpdate = this.options.params = {};
      }

      Object.keys(newParams).forEach(name => {
        if (!skipIfExists || !paramsToUpdate.hasOwnProperty(name)) {
          paramsToUpdate[name] = interpolateParams(newParams[name], paramsToUpdate, this.errors);
        }
      });
    }
  }
/**
 * @return {?}
 */
private _copyOptions() {
    const /** @type {?} */ options: AnimationOptions = {};
    if (this.options) {
      const /** @type {?} */ oldParams = this.options.params;
      if (oldParams) {
        const /** @type {?} */ params: {[name: string]: any} = options['params'] = {};
        Object.keys(this.options.params).forEach(name => { params[name] = oldParams[name]; });
      }
    }
    return options;
  }
/**
 * @param {?=} options
 * @param {?=} element
 * @param {?=} newTime
 * @return {?}
 */
createSubContext(options: AnimationOptions|null = null, element?: any, newTime?: number):
      AnimationTimelineContext {
    const /** @type {?} */ target = element || this.element;
    const /** @type {?} */ context = new AnimationTimelineContext(
        this._driver, target, this.subInstructions, this.errors, this.timelines,
        this.currentTimeline.fork(target, newTime || 0));
    context.previousNode = this.previousNode;
    context.currentAnimateTimings = this.currentAnimateTimings;

    context.options = this._copyOptions();
    context.updateOptions(options);

    context.currentQueryIndex = this.currentQueryIndex;
    context.currentQueryTotal = this.currentQueryTotal;
    context.parentContext = this;
    this.subContextCount++;
    return context;
  }
/**
 * @param {?=} newTime
 * @return {?}
 */
transformIntoNewTimeline(newTime?: number) {
    this.previousNode = DEFAULT_NOOP_PREVIOUS_NODE;
    this.currentTimeline = this.currentTimeline.fork(this.element, newTime);
    this.timelines.push(this.currentTimeline);
    return this.currentTimeline;
  }
/**
 * @param {?} instruction
 * @param {?} duration
 * @param {?} delay
 * @return {?}
 */
appendInstructionToTimeline(
      instruction: AnimationTimelineInstruction, duration: number|null,
      delay: number|null): AnimateTimings {
    const /** @type {?} */ updatedTimings: AnimateTimings = {
      duration: duration != null ? duration : instruction.duration,
      delay: this.currentTimeline.currentTime + (delay != null ? delay : 0) + instruction.delay,
      easing: ''
    };
    const /** @type {?} */ builder = new SubTimelineBuilder(
        instruction.element, instruction.keyframes, instruction.preStyleProps,
        instruction.postStyleProps, updatedTimings, instruction.stretchStartingKeyframe);
    this.timelines.push(builder);
    return updatedTimings;
  }
/**
 * @param {?} time
 * @return {?}
 */
incrementTime(time: number) {
    this.currentTimeline.forwardTime(this.currentTimeline.duration + time);
  }
/**
 * @param {?} delay
 * @return {?}
 */
delayNextStep(delay: number) {
    // negative delays are not yet supported
    if (delay > 0) {
      this.currentTimeline.delayNextStep(delay);
    }
  }
/**
 * @param {?} selector
 * @param {?} originalSelector
 * @param {?} limit
 * @param {?} includeSelf
 * @param {?} optional
 * @param {?} errors
 * @return {?}
 */
invokeQuery(
      selector: string, originalSelector: string, limit: number, includeSelf: boolean,
      optional: boolean, errors: any[]): any[] {
    let /** @type {?} */ results: any[] = [];
    if (includeSelf) {
      results.push(this.element);
    }
    if (selector.length > 0) {  // if :self is only used then the selector is empty
      const /** @type {?} */ multi = limit != 1;
      results.push(...this._driver.query(this.element, selector, multi));
    }

    if (!optional && results.length == 0) {
      errors.push(
          `\`query("${originalSelector}")\` returned zero elements. (Use \`query("${originalSelector}", { optional: true })\` if you wish to allow this.)`);
    }
    return results;
  }
}

function AnimationTimelineContext_tsickle_Closure_declarations() {
/** @type {?} */
AnimationTimelineContext.prototype.parentContext;
/** @type {?} */
AnimationTimelineContext.prototype.currentTimeline;
/** @type {?} */
AnimationTimelineContext.prototype.currentAnimateTimings;
/** @type {?} */
AnimationTimelineContext.prototype.previousNode;
/** @type {?} */
AnimationTimelineContext.prototype.subContextCount;
/** @type {?} */
AnimationTimelineContext.prototype.options;
/** @type {?} */
AnimationTimelineContext.prototype.currentQueryIndex;
/** @type {?} */
AnimationTimelineContext.prototype.currentQueryTotal;
/** @type {?} */
AnimationTimelineContext.prototype.currentStaggerTime;
/** @type {?} */
AnimationTimelineContext.prototype._driver;
/** @type {?} */
AnimationTimelineContext.prototype.element;
/** @type {?} */
AnimationTimelineContext.prototype.subInstructions;
/** @type {?} */
AnimationTimelineContext.prototype.errors;
/** @type {?} */
AnimationTimelineContext.prototype.timelines;
}

export class TimelineBuilder {
public duration: number = 0;
public easing: string|null;
private _previousKeyframe: ɵStyleData = {};
private _currentKeyframe: ɵStyleData = {};
private _keyframes = new Map<number, ɵStyleData>();
private _styleSummary: {[prop: string]: StyleAtTime} = {};
private _localTimelineStyles: ɵStyleData;
private _globalTimelineStyles: ɵStyleData;
private _pendingStyles: ɵStyleData = {};
private _backFill: ɵStyleData = {};
private _currentEmptyStepKeyframe: ɵStyleData|null = null;
/**
 * @param {?} element
 * @param {?} startTime
 * @param {?=} _elementTimelineStylesLookup
 */
constructor(
public element: any,
public startTime: number,
private _elementTimelineStylesLookup?: Map<any, ɵStyleData>) {
    if (!this._elementTimelineStylesLookup) {
      this._elementTimelineStylesLookup = new Map<any, ɵStyleData>();
    }

    this._localTimelineStyles = Object.create(this._backFill, {});
    this._globalTimelineStyles = this._elementTimelineStylesLookup.get(element) !;
    if (!this._globalTimelineStyles) {
      this._globalTimelineStyles = this._localTimelineStyles;
      this._elementTimelineStylesLookup.set(element, this._localTimelineStyles);
    }
    this._loadKeyframe();
  }
/**
 * @return {?}
 */
containsAnimation(): boolean {
    switch (this._keyframes.size) {
      case 0:
        return false;
      case 1:
        return this.getCurrentStyleProperties().length > 0;
      default:
        return true;
    }
  }
/**
 * @return {?}
 */
getCurrentStyleProperties(): string[] { return Object.keys(this._currentKeyframe); }
/**
 * @return {?}
 */
get currentTime() { return this.startTime + this.duration; }
/**
 * @param {?} delay
 * @return {?}
 */
delayNextStep(delay: number) {
    // in the event that a style() step is placed right before a stagger()
    // and that style() step is the very first style() value in the animation
    // then we need to make a copy of the keyframe [0, copy, 1] so that the delay
    // properly applies the style() values to work with the stagger...
    const /** @type {?} */ hasPreStyleStep = this._keyframes.size == 1 && Object.keys(this._pendingStyles).length;

    if (this.duration || hasPreStyleStep) {
      this.forwardTime(this.currentTime + delay);
      if (hasPreStyleStep) {
        this.snapshotCurrentStyles();
      }
    } else {
      this.startTime += delay;
    }
  }
/**
 * @param {?} element
 * @param {?=} currentTime
 * @return {?}
 */
fork(element: any, currentTime?: number): TimelineBuilder {
    this.applyStylesToKeyframe();
    return new TimelineBuilder(
        element, currentTime || this.currentTime, this._elementTimelineStylesLookup);
  }
/**
 * @return {?}
 */
private _loadKeyframe() {
    if (this._currentKeyframe) {
      this._previousKeyframe = this._currentKeyframe;
    }
    this._currentKeyframe = /** @type {?} */(( this._keyframes.get(this.duration)));
    if (!this._currentKeyframe) {
      this._currentKeyframe = Object.create(this._backFill, {});
      this._keyframes.set(this.duration, this._currentKeyframe);
    }
  }
/**
 * @return {?}
 */
forwardFrame() {
    this.duration += ONE_FRAME_IN_MILLISECONDS;
    this._loadKeyframe();
  }
/**
 * @param {?} time
 * @return {?}
 */
forwardTime(time: number) {
    this.applyStylesToKeyframe();
    this.duration = time;
    this._loadKeyframe();
  }
/**
 * @param {?} prop
 * @param {?} value
 * @return {?}
 */
private _updateStyle(prop: string, value: string|number) {
    this._localTimelineStyles[prop] = value;
    this._globalTimelineStyles[prop] = value;
    this._styleSummary[prop] = {time: this.currentTime, value};
  }
/**
 * @return {?}
 */
allowOnlyTimelineStyles() { return this._currentEmptyStepKeyframe !== this._currentKeyframe; }
/**
 * @param {?} easing
 * @return {?}
 */
applyEmptyStep(easing: string|null) {
    if (easing) {
      this._previousKeyframe['easing'] = easing;
    }

    // special case for animate(duration):
    // all missing styles are filled with a `*` value then
    // if any destination styles are filled in later on the same
    // keyframe then they will override the overridden styles
    // We use `_globalTimelineStyles` here because there may be
    // styles in previous keyframes that are not present in this timeline
    Object.keys(this._globalTimelineStyles).forEach(prop => {
      this._backFill[prop] = this._globalTimelineStyles[prop] || AUTO_STYLE;
      this._currentKeyframe[prop] = AUTO_STYLE;
    });
    this._currentEmptyStepKeyframe = this._currentKeyframe;
  }
/**
 * @param {?} input
 * @param {?} easing
 * @param {?} errors
 * @param {?=} options
 * @return {?}
 */
setStyles(
      input: (ɵStyleData|string)[], easing: string|null, errors: any[],
      options?: AnimationOptions) {
    if (easing) {
      this._previousKeyframe['easing'] = easing;
    }

    const /** @type {?} */ params = (options && options.params) || {};
    const /** @type {?} */ styles = flattenStyles(input, this._globalTimelineStyles);
    Object.keys(styles).forEach(prop => {
      const /** @type {?} */ val = interpolateParams(styles[prop], params, errors);
      this._pendingStyles[prop] = val;
      if (!this._localTimelineStyles.hasOwnProperty(prop)) {
        this._backFill[prop] = this._globalTimelineStyles.hasOwnProperty(prop) ?
            this._globalTimelineStyles[prop] :
            AUTO_STYLE;
      }
      this._updateStyle(prop, val);
    });
  }
/**
 * @return {?}
 */
applyStylesToKeyframe() {
    const /** @type {?} */ styles = this._pendingStyles;
    const /** @type {?} */ props = Object.keys(styles);
    if (props.length == 0) return;

    this._pendingStyles = {};

    props.forEach(prop => {
      const /** @type {?} */ val = styles[prop];
      this._currentKeyframe[prop] = val;
    });

    Object.keys(this._localTimelineStyles).forEach(prop => {
      if (!this._currentKeyframe.hasOwnProperty(prop)) {
        this._currentKeyframe[prop] = this._localTimelineStyles[prop];
      }
    });
  }
/**
 * @return {?}
 */
snapshotCurrentStyles() {
    Object.keys(this._localTimelineStyles).forEach(prop => {
      const /** @type {?} */ val = this._localTimelineStyles[prop];
      this._pendingStyles[prop] = val;
      this._updateStyle(prop, val);
    });
  }
/**
 * @return {?}
 */
getFinalKeyframe() { return this._keyframes.get(this.duration); }
/**
 * @return {?}
 */
get properties() {
    const /** @type {?} */ properties: string[] = [];
    for (let /** @type {?} */ prop in this._currentKeyframe) {
      properties.push(prop);
    }
    return properties;
  }
/**
 * @param {?} timeline
 * @return {?}
 */
mergeTimelineCollectedStyles(timeline: TimelineBuilder) {
    Object.keys(timeline._styleSummary).forEach(prop => {
      const /** @type {?} */ details0 = this._styleSummary[prop];
      const /** @type {?} */ details1 = timeline._styleSummary[prop];
      if (!details0 || details1.time > details0.time) {
        this._updateStyle(prop, details1.value);
      }
    });
  }
/**
 * @return {?}
 */
buildKeyframes(): AnimationTimelineInstruction {
    this.applyStylesToKeyframe();
    const /** @type {?} */ preStyleProps = new Set<string>();
    const /** @type {?} */ postStyleProps = new Set<string>();
    const /** @type {?} */ isEmpty = this._keyframes.size === 1 && this.duration === 0;

    let /** @type {?} */ finalKeyframes: ɵStyleData[] = [];
    this._keyframes.forEach((keyframe, time) => {
      const /** @type {?} */ finalKeyframe = copyStyles(keyframe, true);
      Object.keys(finalKeyframe).forEach(prop => {
        const /** @type {?} */ value = finalKeyframe[prop];
        if (value == PRE_STYLE) {
          preStyleProps.add(prop);
        } else if (value == AUTO_STYLE) {
          postStyleProps.add(prop);
        }
      });
      if (!isEmpty) {
        finalKeyframe['offset'] = time / this.duration;
      }
      finalKeyframes.push(finalKeyframe);
    });

    const /** @type {?} */ preProps: string[] = preStyleProps.size ? iteratorToArray(preStyleProps.values()) : [];
    const /** @type {?} */ postProps: string[] = postStyleProps.size ? iteratorToArray(postStyleProps.values()) : [];

    // special case for a 0-second animation (which is designed just to place styles onscreen)
    if (isEmpty) {
      const /** @type {?} */ kf0 = finalKeyframes[0];
      const /** @type {?} */ kf1 = copyObj(kf0);
      kf0['offset'] = 0;
      kf1['offset'] = 1;
      finalKeyframes = [kf0, kf1];
    }

    return createTimelineInstruction(
        this.element, finalKeyframes, preProps, postProps, this.duration, this.startTime,
        this.easing, false);
  }
}

function TimelineBuilder_tsickle_Closure_declarations() {
/** @type {?} */
TimelineBuilder.prototype.duration;
/** @type {?} */
TimelineBuilder.prototype.easing;
/** @type {?} */
TimelineBuilder.prototype._previousKeyframe;
/** @type {?} */
TimelineBuilder.prototype._currentKeyframe;
/** @type {?} */
TimelineBuilder.prototype._keyframes;
/** @type {?} */
TimelineBuilder.prototype._styleSummary;
/** @type {?} */
TimelineBuilder.prototype._localTimelineStyles;
/** @type {?} */
TimelineBuilder.prototype._globalTimelineStyles;
/** @type {?} */
TimelineBuilder.prototype._pendingStyles;
/** @type {?} */
TimelineBuilder.prototype._backFill;
/** @type {?} */
TimelineBuilder.prototype._currentEmptyStepKeyframe;
/** @type {?} */
TimelineBuilder.prototype.element;
/** @type {?} */
TimelineBuilder.prototype.startTime;
/** @type {?} */
TimelineBuilder.prototype._elementTimelineStylesLookup;
}

class SubTimelineBuilder extends TimelineBuilder {
public timings: AnimateTimings;
/**
 * @param {?} element
 * @param {?} keyframes
 * @param {?} preStyleProps
 * @param {?} postStyleProps
 * @param {?} timings
 * @param {?=} _stretchStartingKeyframe
 */
constructor(
public element: any,
public keyframes: ɵStyleData[],
public preStyleProps: string[],
public postStyleProps: string[], timings: AnimateTimings,
private _stretchStartingKeyframe: boolean = false) {
    super(element, timings.delay);
    this.timings = {duration: timings.duration, delay: timings.delay, easing: timings.easing};
  }
/**
 * @return {?}
 */
containsAnimation(): boolean { return this.keyframes.length > 1; }
/**
 * @return {?}
 */
buildKeyframes(): AnimationTimelineInstruction {
    let /** @type {?} */ keyframes = this.keyframes;
    let {delay, duration, easing} = this.timings;
    if (this._stretchStartingKeyframe && delay) {
      const /** @type {?} */ newKeyframes: ɵStyleData[] = [];
      const /** @type {?} */ totalTime = duration + delay;
      const /** @type {?} */ startingGap = delay / totalTime;

      // the original starting keyframe now starts once the delay is done
      const /** @type {?} */ newFirstKeyframe = copyStyles(keyframes[0], false);
      newFirstKeyframe['offset'] = 0;
      newKeyframes.push(newFirstKeyframe);

      const /** @type {?} */ oldFirstKeyframe = copyStyles(keyframes[0], false);
      oldFirstKeyframe['offset'] = roundOffset(startingGap);
      newKeyframes.push(oldFirstKeyframe);

      /*
        When the keyframe is stretched then it means that the delay before the animation
        starts is gone. Instead the first keyframe is placed at the start of the animation
        and it is then copied to where it starts when the original delay is over. This basically
        means nothing animates during that delay, but the styles are still renderered. For this
        to work the original offset values that exist in the original keyframes must be "warped"
        so that they can take the new keyframe + delay into account.

        delay=1000, duration=1000, keyframes = 0 .5 1

        turns into

        delay=0, duration=2000, keyframes = 0 .33 .66 1
       */

      // offsets between 1 ... n -1 are all warped by the keyframe stretch
      const /** @type {?} */ limit = keyframes.length - 1;
      for (let /** @type {?} */ i = 1; i <= limit; i++) {
        let /** @type {?} */ kf = copyStyles(keyframes[i], false);
        const /** @type {?} */ oldOffset = /** @type {?} */(( kf['offset'] as number));
        const /** @type {?} */ timeAtKeyframe = delay + oldOffset * duration;
        kf['offset'] = roundOffset(timeAtKeyframe / totalTime);
        newKeyframes.push(kf);
      }

      // the new starting keyframe should be added at the start
      duration = totalTime;
      delay = 0;
      easing = '';

      keyframes = newKeyframes;
    }

    return createTimelineInstruction(
        this.element, keyframes, this.preStyleProps, this.postStyleProps, duration, delay, easing,
        true);
  }
}

function SubTimelineBuilder_tsickle_Closure_declarations() {
/** @type {?} */
SubTimelineBuilder.prototype.timings;
/** @type {?} */
SubTimelineBuilder.prototype.element;
/** @type {?} */
SubTimelineBuilder.prototype.keyframes;
/** @type {?} */
SubTimelineBuilder.prototype.preStyleProps;
/** @type {?} */
SubTimelineBuilder.prototype.postStyleProps;
/** @type {?} */
SubTimelineBuilder.prototype._stretchStartingKeyframe;
}

/**
 * @param {?} offset
 * @param {?=} decimalPoints
 * @return {?}
 */
function roundOffset(offset: number, decimalPoints = 3): number {
  const /** @type {?} */ mult = Math.pow(10, decimalPoints - 1);
  return Math.round(offset * mult) / mult;
}
/**
 * @param {?} input
 * @param {?} allStyles
 * @return {?}
 */
function flattenStyles(input: (ɵStyleData | string)[], allStyles: ɵStyleData) {
  const /** @type {?} */ styles: ɵStyleData = {};
  let /** @type {?} */ allProperties: string[];
  input.forEach(token => {
    if (token === '*') {
      allProperties = allProperties || Object.keys(allStyles);
      allProperties.forEach(prop => { styles[prop] = AUTO_STYLE; });
    } else {
      copyStyles( /** @type {?} */((token as ɵStyleData)), false, styles);
    }
  });
  return styles;
}



// WEBPACK FOOTER //
// ../../../../../packages/animations/browser/src/dsl/animation_timeline_builder.ts